import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PaymentRequest } from '../../../types/organization.types'

interface OrganizationPaymentsState {
  payments: PaymentRequest[]
  loading: boolean
  error: string | null
  selectedPayment: PaymentRequest | null
  initiatingPayment: boolean
  verifyingPaymentId: string | null
  cancellingPaymentId: string | null
  confirmingPaymentId: string | null
  totalCount: number
  currentPage: number
}

const initialState: OrganizationPaymentsState = {
  payments: [],
  loading: false,
  error: null,
  selectedPayment: null,
  initiatingPayment: false,
  verifyingPaymentId: null,
  cancellingPaymentId: null,
  confirmingPaymentId: null,
  totalCount: 0,
  currentPage: 1,
}

const organizationPaymentsSlice = createSlice({
  name: 'organizationPayments',
  initialState,
  reducers: {
    // Fetch payments
    fetchPaymentsStart(state) {
      state.loading = true
      state.error = null
    },
    fetchPaymentsSuccess(
      state,
      action: PayloadAction<{ payments: PaymentRequest[]; count: number }>
    ) {
      state.loading = false
      state.payments = action.payload.payments
      state.totalCount = action.payload.count
      state.error = null
    },
    fetchPaymentsFailure(state, action: PayloadAction<string>) {
      state.loading = false
      state.error = action.payload
    },

    // Initiate payment
    initiatePaymentStart(state) {
      state.initiatingPayment = true
      state.error = null
    },
    initiatePaymentSuccess(state, action: PayloadAction<PaymentRequest>) {
      state.initiatingPayment = false
      state.payments.unshift(action.payload)
      state.selectedPayment = action.payload
      state.error = null
    },
    initiatePaymentFailure(state, action: PayloadAction<string>) {
      state.initiatingPayment = false
      state.error = action.payload
    },

    // Verify payment
    verifyPaymentStart(state, action: PayloadAction<string>) {
      state.verifyingPaymentId = action.payload
      state.error = null
    },
    verifyPaymentSuccess(state, action: PayloadAction<PaymentRequest>) {
      state.verifyingPaymentId = null
      const index = state.payments.findIndex((p) => p.id === action.payload.id)
      if (index !== -1) {
        state.payments[index] = action.payload
      }
      if (state.selectedPayment?.id === action.payload.id) {
        state.selectedPayment = action.payload
      }
      state.error = null
    },
    verifyPaymentFailure(state, action: PayloadAction<string>) {
      state.verifyingPaymentId = null
      state.error = action.payload
    },

    // Cancel payment
    cancelPaymentStart(state, action: PayloadAction<string>) {
      state.cancellingPaymentId = action.payload
      state.error = null
    },
    cancelPaymentSuccess(state, action: PayloadAction<PaymentRequest>) {
      state.cancellingPaymentId = null
      const index = state.payments.findIndex((p) => p.id === action.payload.id)
      if (index !== -1) {
        state.payments[index] = action.payload
      }
      if (state.selectedPayment?.id === action.payload.id) {
        state.selectedPayment = action.payload
      }
      state.error = null
    },
    cancelPaymentFailure(state, action: PayloadAction<string>) {
      state.cancellingPaymentId = null
      state.error = action.payload
    },

    // Confirm demo payment
    confirmDemoPaymentStart(state, action: PayloadAction<string>) {
      state.confirmingPaymentId = action.payload
      state.error = null
    },
    confirmDemoPaymentSuccess(state, action: PayloadAction<PaymentRequest>) {
      state.confirmingPaymentId = null
      const index = state.payments.findIndex((p) => p.id === action.payload.id)
      if (index !== -1) {
        state.payments[index] = action.payload
      }
      if (state.selectedPayment?.id === action.payload.id) {
        state.selectedPayment = action.payload
      }
      state.error = null
    },
    confirmDemoPaymentFailure(state, action: PayloadAction<string>) {
      state.confirmingPaymentId = null
      state.error = action.payload
    },

    // Select payment
    selectPayment(state, action: PayloadAction<PaymentRequest | null>) {
      state.selectedPayment = action.payload
    },

    // Set page
    setCurrentPage(state, action: PayloadAction<number>) {
      state.currentPage = action.payload
    },

    // Clear error
    clearError(state) {
      state.error = null
    },

    // Reset state
    resetPaymentsState: () => initialState,
  },
})

export const {
  fetchPaymentsStart,
  fetchPaymentsSuccess,
  fetchPaymentsFailure,
  initiatePaymentStart,
  initiatePaymentSuccess,
  initiatePaymentFailure,
  verifyPaymentStart,
  verifyPaymentSuccess,
  verifyPaymentFailure,
  cancelPaymentStart,
  cancelPaymentSuccess,
  cancelPaymentFailure,
  confirmDemoPaymentStart,
  confirmDemoPaymentSuccess,
  confirmDemoPaymentFailure,
  selectPayment,
  setCurrentPage,
  clearError,
  resetPaymentsState,
} = organizationPaymentsSlice.actions

export default organizationPaymentsSlice.reducer
